import NavSupervisor from "@/nav/nav.supervisor"
import { ChangeEvent, useRef, useState } from "react"
import Card from "@/components/card"
import TextInput from "@/components/text-input"
import Button from "@/components/button"
import UploadButtonWidget from "@/widgets/upload-button.widget"
import env from "@/env"
import Link from "@/components/link"
import LoadingModal from "@/modals/loading.modal"
import CongratulationModal from "@/modals/congratulation.modal"

export default function SupervisorDemoPrepareRoute() {
  const [name, setName] = useState("")
  const [step, setStep] = useState<1 | 2>(1)
  const [isLoading, setIsLoading] = useState(false)
  const [presentationUrl, setPresentationUrl] = useState("")
  const [error, setError] = useState("")
  const submitting = useRef(false)

  async function uploadLogo(input: File[] | ChangeEvent<HTMLInputElement>) {
    const file = Array.isArray(input) ? input[0] : input.target.files?.[0]
    if (!file) return
    if (!/\.(png|jpe?g|webp|svg)$/i.test(file.name) || file.size > 2000000) {
      setError("Choisissez un logo PNG, JPG, WEBP ou SVG de 2 Mo maximum.")
      return
    }
    await generateSupport(file)
  }

  async function generateSupport(file: File) {
    if (!name.trim() || submitting.current) return
    submitting.current = true
    setIsLoading(true)
    setError("")
    try {
      const body = new FormData()
      body.append("name", name.trim())
      body.append("logo", file)
      const response = await fetch(`${env.REACT_APP_N8N_URL}/webhook/generate-demo-support`, {
        method: "POST",
        body
      })
      if (!response.ok) throw new Error()
      const data: unknown = await response.json()
      if (!data || typeof data !== "object" || !("url" in data) || typeof data.url !== "string") {
        throw new Error()
      }
      const url = new URL(data.url)
      if (url.protocol !== "https:" || url.hostname !== "docs.google.com" || !url.pathname.startsWith("/presentation/")) {
        throw new Error()
      }
      setPresentationUrl(url.href)
    } catch {
      //Same message for every error (network, missing or invalid link)
      setError("La demande n’a pas pu être envoyée. Sélectionnez à nouveau le logo pour réessayer.")
    } finally {
      submitting.current = false
      setIsLoading(false)
    }
  }

  return (
    <NavSupervisor>
      <div className="flex flex-1 items-center justify-center p-[30px]">
        <Card
          className="mb-0 w-full max-w-[600px]"
          title={<h1 className="text-lg">Préparer une démonstration</h1>}>
          <div className="flex flex-col gap-[20px]">
            {step === 1 ? (
              <>
                <TextInput
                  title="Nom du compte"
                  value={name}
                  onChange={({ value }: { value: string }) => setName(value)}
                  isFullWidth
                />
                <div className="flex justify-end">
                  <Button className="primary" disabled={!name.trim()} onClick={() => setStep(2)}>
                    Continuer
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-1">
                  <span>Compte :</span>
                  <Link isWithoutMargin onClick={() => {
                    if (submitting.current) return
                    setError("")
                    setStep(1)
                  }}>{name.trim()}</Link>
                </div>
                <fieldset disabled={isLoading} className="m-0 min-w-0 border-0 p-0">
                  <div className="[&_.upload-button]:pb-[30px]">
                    <UploadButtonWidget title="Logo" acceptedFiles=".png,.jpg,.jpeg,.webp,.svg" onUpload={uploadLogo} />
                  </div>
                </fieldset>
              </>
            )}
            {error && <p role="alert" className="text-red-600">{error}</p>}
          </div>
        </Card>
      </div>
      {isLoading && <LoadingModal />}
      {presentationUrl && (
        <CongratulationModal
          text={`La présentation pour ${name.trim()} est prête !`}
          onCloseText="Ouvrir la présentation"
          onClose={() => {
            window.open(presentationUrl, "_blank", "noopener,noreferrer")
            setPresentationUrl("")
          }}
        />
      )}
    </NavSupervisor>
  )
}
